import { Card } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { Ingredient, Step, CuisineType, FoodType } from "@/types/recipe";
import RecipeHeaderActions from "./recipe/RecipeHeaderActions";
import RecipeSteps from "./recipe/RecipeSteps";
import IngredientsList from "./recipe/IngredientsList";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Clock, ClipboardList } from "lucide-react";
import CookMode from "./recipe/CookMode";
import NutritionalInfo from "./recipe/NutritionalInfo";
import { Button } from "./ui/button";
import CookingCards from "./recipe/CookingCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sun } from "lucide-react";

interface Author {
  username: string;
  avatarUrl: string | null;
}

interface RecipeProps {
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  ingredients: Ingredient[];
  steps: Step[] | string[];
  onPrint: () => void;
  author: Author;
  recipeId: string;
  userId: string;
  cuisineType?: CuisineType | null;
  foodType?: FoodType | null;
  defaultServings?: number;
  onServingsChange?: (servings: number) => void;
  nutritionalValues: any;
}

export const Recipe = ({ 
  title, 
  description, 
  imageUrl, 
  ingredients, 
  steps, 
  onPrint,
  author,
  recipeId,
  userId,
  cuisineType,
  foodType,
  defaultServings = 1,
  onServingsChange,
  nutritionalValues
}: RecipeProps) => {
  const [temperatureUnit, setTemperatureUnit] = useState<"C" | "F">("F");
  const [isFavorited, setIsFavorited] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const normalizedUsername = author.username.toLowerCase().replace(/\s+/g, '');
  const isHomePage = !recipeId;
  const [currentServings, setCurrentServings] = useState<number>(defaultServings);
  const [isCookingCardOpen, setIsCookingCardOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const savedUnit = localStorage.getItem("temperatureUnit");
    if (savedUnit === "C" || savedUnit === "F") {
      setTemperatureUnit(savedUnit);
    }
    setShareUrl(window.location.href);
    if (recipeId) {
      checkIfFavorited();
    }
  }, [recipeId]);

  const checkIfFavorited = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: favorite, error } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('recipe_id', recipeId)
        .maybeSingle();

      if (error) {
        console.error('Error checking favorite status:', error);
        return;
      }

      setIsFavorited(!!favorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const handleFavorite = async () => {
    if (!recipeId) {
      toast.error("Demo recipes cannot be favorited");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please sign in to favorite recipes");
        return;
      }

      if (isFavorited) {
        // Remove from favorites and likes
        const { error: favError } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('recipe_id', recipeId);
        
        const { error: likeError } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', user.id)
          .eq('recipe_id', recipeId);

        if (favError || likeError) {
          console.error('Error removing favorite/like:', favError || likeError);
          toast.error("Failed to remove from favorites");
          return;
        }

        setIsFavorited(false);
        toast.success("Recipe removed from favorites");
      } else {
        // Add to favorites and likes
        const { error: favError } = await supabase
          .from('favorites')
          .insert([{ 
            user_id: user.id, 
            recipe_id: recipeId 
          }]);

        const { error: likeError } = await supabase
          .from('likes')
          .insert([{
            user_id: user.id,
            recipe_id: recipeId
          }]);

        if (favError || likeError) {
          console.error('Error adding favorite/like:', favError || likeError);
          toast.error("Failed to add to favorites");
          return;
        }

        setIsFavorited(true);
        toast.success("Recipe added to favorites");
      }
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast.error("Failed to update favorite status");
    }
  };

  const calculateTotalTime = () => {
    let totalMinutes = 0;
    steps.forEach((step) => {
      if (typeof step === 'object' && step.time) {
        const match = step.time.match(/(\d+)\s*minutes?/);
        if (match) {
          totalMinutes += parseInt(match[1]);
        }
      }
    });
    
    if (totalMinutes === 0) return null;
    
    if (totalMinutes < 60) {
      return `${totalMinutes} minutes`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (minutes === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} minutes`;
    }
  };

  const handleServingsChange = (newServings: number) => {
    setCurrentServings(newServings);
    if (onServingsChange) {
      onServingsChange(newServings);
    }
  };

  const handleCookingCard = () => {
    setIsCookingCardOpen(true);
  };

  const totalTime = calculateTotalTime();

  // Helper function to check if nutritional values are valid
  const hasValidNutritionalValues = () => {
    if (!nutritionalValues) return false;
    
    // Check if any of the nutritional values are non-zero
    return Object.values(nutritionalValues).some(value => 
      typeof value === 'number' && value > 0
    );
  };

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .recipe-print-section,
            .recipe-print-section * {
              visibility: visible;
            }
            .recipe-print-section {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .recipe-print-header {
              text-align: center;
              margin-bottom: 2rem;
            }
            .recipe-print-header img {
              height: 4rem;
              margin: 0 auto;
            }
            .recipe-print-footer {
              text-align: center;
              margin-top: 2rem;
              font-size: 0.875rem;
              color: #666;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <div className="w-full max-w-6xl mx-auto p-4 md:p-6 space-y-8 recipe-print-section">
        <div className="recipe-print-header">
          <img 
            src="/lovable-uploads/477186b7-9af3-4bec-a238-82b021f153b9.png" 
            alt="Airfryer AI" 
            className="hidden print:block"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="space-y-3">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">{title}</h1>
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-500">
                by{" "}
                {isHomePage ? (
                  <span className="text-primary">
                    Puff the Dragon
                  </span>
                ) : (
                  <a 
                    href={`/${normalizedUsername}`}
                    className="text-primary hover:underline no-print"
                  >
                    {author.username}
                  </a>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {cuisineType && (
                  <span className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
                    {cuisineType} Cuisine
                  </span>
                )}
                {foodType && (
                  <span className="inline-block bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
                    {foodType}
                  </span>
                )}
                {totalTime && (
                  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    {totalTime}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="no-print">
            <RecipeHeaderActions
              shareUrl={shareUrl}
              isLiked={isFavorited}
              onLike={handleFavorite}
              onPrint={onPrint}
              recipeId={recipeId}
              userId={userId}
              disableLike={isHomePage}
            />
          </div>
        </div>

        {imageUrl && (
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 shadow-lg">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {description && (
          <Card className="p-4 md:p-6 shadow-lg bg-white/80 backdrop-blur-sm border-2">
            <h2 className="text-xl font-medium text-gray-900 mb-2">Description</h2>
            <p className="text-gray-700">{description}</p>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-4 md:gap-8">
          <div className="order-2 md:order-1 space-y-4">
            <Card className="p-4 md:p-6 space-y-4 shadow-lg bg-white/80 backdrop-blur-sm rounded-xl border-2 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-medium text-gray-900">Instructions</h2>
                <div className="flex items-center gap-2 no-print">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCookingCard}
                    className="flex items-center gap-2"
                  >
                    <ClipboardList className="w-4 h-4" />
                    <span className="md:inline hidden">Cooking Cards</span>
                  </Button>
                  <CookMode />
                </div>
              </div>
              <RecipeSteps steps={steps} temperatureUnit={temperatureUnit} />
            </Card>
          </div>

          <div className="order-1 md:order-2 space-y-4">
            <Card className="p-4 md:p-6 shadow-lg bg-white/80 backdrop-blur-sm rounded-xl border-2 hover:shadow-xl transition-shadow">
              <h2 className="text-xl font-medium text-gray-900 mb-4">Ingredients</h2>
              <IngredientsList 
                ingredients={ingredients} 
                defaultServings={defaultServings}
                onServingsChange={handleServingsChange}
              />
            </Card>
          </div>
        </div>

        {hasValidNutritionalValues() && (
          <Card className="p-4 md:p-6 shadow-lg bg-white/80 backdrop-blur-sm rounded-xl border-2">
            <NutritionalInfo 
              nutritionalValues={nutritionalValues}
              servings={currentServings}
              defaultServings={defaultServings}
            />
          </Card>
        )}

        <div className="recipe-print-footer hidden print:block">
          <p>Find this recipe and more at https://airfryer.ai</p>
        </div>
      </div>

      <CookingCards
        isOpen={isCookingCardOpen}
        onClose={() => setIsCookingCardOpen(false)}
        steps={steps}
        ingredients={ingredients}
        temperatureUnit={temperatureUnit}
      />
    </>
  );
};
